import { AxiosError } from 'axios';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  ApplicantExtended,
  FormStatuses,
  MembersDocument,
  PersonaType,
  RecommenderDocument,
  SubjectArea,
  VerifyRecommendersResponse,
} from '@youscience/commonapp-service-common';

import useApplyingSchoolsPlannerStore from '@store/ApplyingSchoolsPlannerStore';
import useNotificationStore from '@store/NotificationStore';
import useRecommenderStore from '@store/RecommenderStore';

import { commonAppService } from '@services/commonApp.service';

interface CommonAppStore {
  isLoading: boolean;
  commonAppConnected?: boolean;
  commonAppLogin?: string;
  applicant: ApplicantExtended;
  formStatuses: FormStatuses;
  recommenders: RecommenderDocument[];
  getApplicant: () => Promise<ApplicantExtended>;
  updateApplicant: (applicant: { requestId: string; applicantId: string }) => void;
  createCommonAppLogin: (ceebCode?: string) => Promise<string>;
  getCommonAppLogin: () => Promise<string>;
  getFerpaLink: () => Promise<string>;
  getFormStatuses: () => Promise<FormStatuses>;
  removeCounselor: () => Promise<void>;
  addCounselor: (userId: string) => Promise<void>;
  inviteTeacher: (userId: string, subject: SubjectArea, memberId?: number) => Promise<void>;
  editTeacher: (recommenderId: number, subject: string) => Promise<void>;
  uninviteTeacher: (recommender: RecommenderDocument) => Promise<void>;
  assignTeacher: (recommenderId: number, memberId: number) => Promise<void>;
  unassignTeacher: (recommenderId: number, memberId: number) => Promise<void>;
  getSchoolInfo: (memberId: number) => Promise<Record<string, unknown>>;
  getMembers: (memberIds: number[]) => Promise<MembersDocument[]>;
  isFerpaSigned: () => boolean;
}

// Internal loading state tracking
type LoadingOperation =
  | 'getApplicant'
  | 'getFormStatuses'
  | 'getCommonAppLogin'
  | 'createCommonAppLogin'
  | 'getFerpaLink'
  | 'removeCounselor'
  | 'addCounselor'
  | 'inviteTeacher'
  | 'editTeacher'
  | 'uninviteTeacher'
  | 'assignTeacher'
  | 'unassignTeacher'
  | 'getMembers';

const initialState = {
  isLoading: false,
  commonAppConnected: false,
} as CommonAppStore;

const loadingOperations = new Set<LoadingOperation>();

const setLoading = (
  operation: LoadingOperation,
  isLoading: boolean,
  set: (partial: Partial<CommonAppStore>) => void,
) => {
  if (isLoading) {
    loadingOperations.add(operation);
  } else {
    loadingOperations.delete(operation);
  }
  set({ isLoading: loadingOperations.size > 0 });
};

const updateCommonAppConnectionStatus = (state: CommonAppStore, set: (partial: Partial<CommonAppStore>) => void) => {
  const accountIsLinked = !!state.applicant?.applicantId;
  const collegeSelected = (state.applicant?.members?.length ?? 0) > 0;

  set({
    commonAppConnected: accountIsLinked && state.isFerpaSigned() && collegeSelected,
  });
};

export const useCommonAppStore = createWithEqualityFn<CommonAppStore>()(
  (set, get) => ({
    ...initialState,

    updateApplicant: async (applicant: { requestId: string; applicantId: string }) => {
      const applicantDetails = await commonAppService.patchAsync<
        ApplicantExtended,
        { requestId: string; applicantId: string }
      >(`applicant`, {
        ...applicant,
      });

      set({
        applicant: applicantDetails,
      });

      updateCommonAppConnectionStatus(get(), set);
    },

    isFerpaSigned: () => {
      const { applicant } = get();

      return applicant?.ferpaStatus === 1 || applicant?.ferpaStatus === 2;
    },

    getFormStatuses: async () => {
      setLoading('getFormStatuses', true, set);

      const formStatuses = await commonAppService.getAsync<FormStatuses>(`applicant/form-status`);

      set({
        formStatuses,
      });

      setLoading('getFormStatuses', false, set);
      return formStatuses;
    },

    getApplicant: async () => {
      let { applicant } = get();

      setLoading('getApplicant', true, set);

      try {
        applicant = await commonAppService.getAsync<ApplicantExtended>(`applicant`);
        useApplyingSchoolsPlannerStore.getState().getAppliedSchool();

        if (Object.keys(applicant).length === 0) {
          setLoading('getApplicant', false, set);
          return applicant;
        }

        if (!applicant.schoolCode) {
          const applicantVerified = await commonAppService.patchAsync<
            VerifyRecommendersResponse,
            Record<string, never>
          >(`applicant/verify-recommenders`, {});
          applicant = applicantVerified.applicant as ApplicantExtended;
        }

        await get().getFormStatuses();
      } catch (error) {
        const { response } = error as AxiosError;

        if (response?.status !== 404) {
          throw error;
        } else {
          applicant = { noPermissionGranted: true } as ApplicantExtended;
        }
      }

      set({
        applicant,
      });

      updateCommonAppConnectionStatus(get(), set);
      setLoading('getApplicant', false, set);

      return applicant;
    },

    getCommonAppLogin: async () => {
      setLoading('getCommonAppLogin', true, set);

      try {
        const result = await commonAppService.getAsync<string>(`login`);

        set({
          commonAppLogin: result,
        });

        setLoading('getCommonAppLogin', false, set);
        return result;
      } catch (error) {
        setLoading('getCommonAppLogin', false, set);
        return '';
      }
    },

    createCommonAppLogin: async (ceebCode?: string) => {
      setLoading('createCommonAppLogin', true, set);

      const result = await commonAppService.getAsync<string>(
        `create-account-link${ceebCode ? `?ceebCode=${ceebCode}` : ''}`,
      );

      setLoading('createCommonAppLogin', false, set);
      return result;
    },

    getFerpaLink: async () => {
      setLoading('getFerpaLink', true, set);

      const result = await commonAppService.getAsync<string>(`ferpa-link?applicantId=${get().applicant.applicantId}`);

      setLoading('getFerpaLink', false, set);
      return result;
    },

    removeCounselor: async () => {
      const { applicant } = get();
      const { counselor } = applicant;

      if (!counselor) return;

      setLoading('removeCounselor', true, set);

      let response;

      try {
        response = await commonAppService.patchAsync<ApplicantExtended, { recommenderId: number; applicantId: number }>(
          `remove-counselor`,
          {
            recommenderId: counselor.recommenderId,
            applicantId: applicant.applicantId ?? 0,
          },
        );
      } catch (error: unknown) {
        if ((error as AxiosError).status === 400) {
          useNotificationStore.getState().notify({
            message:
              'To protect the integrity of your application, this counselor cannot be removed. A recommendation or report has been submitted to Common App on your behalf. Please contact YouScience customer support if you believe this is an error.',
            severity: 'info',
          });
        }

        setLoading('removeCounselor', false, set);
        return;
      }

      await get().getFormStatuses();

      set({
        applicant: response,
      });

      useNotificationStore.getState().notify({
        title: 'Counselor removed',
        message: `${counselor?.firstName} ${counselor?.lastName} was removed as a counselor.`,
        severity: 'success',
      });

      setLoading('removeCounselor', false, set);
    },

    addCounselor: async (userId: string) => {
      setLoading('addCounselor', true, set);

      try {
        const applicant = await commonAppService.patchAsync<ApplicantExtended, { userId: string }>(`add-counselor`, {
          userId,
        });

        await get().getFormStatuses();

        set({
          applicant,
        });

        const { counselor } = applicant;

        useNotificationStore.getState().notify({
          title: 'Invitation sent',
          message: `${counselor?.firstName as string} ${counselor?.lastName as string} was invited as a counselor.`,
          severity: 'success',
        });
      } catch (e) {
        useNotificationStore.getState().notify({
          message: 'There was an error adding the counselor',
          severity: 'error',
        });
        throw e;
      } finally {
        setLoading('addCounselor', false, set);
      }
    },

    editTeacher: async (recommenderId: number, subject: string) => {
      setLoading('editTeacher', true, set);

      try {
        const applicant = await commonAppService
          .patchAsync<ApplicantExtended, { recommenderId: number; subject: string }>(`applicant/teacher`, {
            recommenderId,
            subject,
          })
          .catch((e) => {
            useNotificationStore
              .getState()
              .notify({ message: 'There was an error updating the teacher', severity: 'error' });

            throw e;
          });

        useRecommenderStore.getState().getRecommenders();
        await get().getFormStatuses();

        set({
          applicant,
        });

        const { teachers } = applicant;

        const teacher = teachers?.find((t) => t.recommenderId === recommenderId);

        useNotificationStore.getState().notify({
          title: 'Invitation sent',
          message: `${teacher?.firstName as string} ${teacher?.lastName as string} was invited as a recommender.`,
          severity: 'success',
        });
      } finally {
        setLoading('editTeacher', false, set);
      }
    },

    inviteTeacher: async (userId: string, subject: SubjectArea, memberId?: number) => {
      setLoading('inviteTeacher', true, set);

      try {
        const applicant = await commonAppService
          .patchAsync<ApplicantExtended, { userId: string; subject: SubjectArea; memberId?: number }>(
            `invite-teacher`,
            {
              userId,
              subject,
              memberId,
            },
          )
          .catch((e) => {
            const errorMessage = e.response?.data?.error || e.message || 'There was an error inviting the teacher';
            useNotificationStore.getState().notify({ message: errorMessage, severity: 'error' });

            throw e;
          });

        await get().getFormStatuses();

        set({
          applicant,
        });

        const { teachers } = applicant;

        const teacher = teachers?.find((t) => t.userId === userId);

        useNotificationStore.getState().notify({
          title: 'Invitation sent',
          message: `${teacher?.firstName as string} ${teacher?.lastName as string} was invited as a recommender.`,
          severity: 'success',
        });
      } finally {
        setLoading('inviteTeacher', false, set);
      }
    },

    uninviteTeacher: async (recommender: RecommenderDocument) => {
      setLoading('uninviteTeacher', true, set);

      try {
        const applicant = await commonAppService.patchAsync<
          ApplicantExtended,
          { recommenderId: number; applicantId: string }
        >(`remove-teacher`, {
          recommenderId: recommender.recommenderId,
          applicantId: recommender.applicantId.toString(),
        });

        await get().getFormStatuses();

        set({
          applicant,
        });

        useNotificationStore.getState().notify({
          title: 'Teacher removed',
          message: `${recommender.firstName} ${recommender.lastName} was removed as a recommender.`,
          severity: 'success',
        });
      } finally {
        setLoading('uninviteTeacher', false, set);
      }
    },

    assignTeacher: async (recommenderId: number, memberId: number) => {
      setLoading('assignTeacher', true, set);

      try {
        const applicant = await commonAppService
          .patchAsync<ApplicantExtended, { recommenderId: number; memberId: number }>(`assign-teacher`, {
            recommenderId,
            memberId,
          })
          .catch((e) => {
            useNotificationStore
              .getState()
              .notify({ message: 'There was an error inviting the teacher', severity: 'error' });

            throw e;
          });

        await get().getFormStatuses();

        set({
          applicant,
        });

        const { teachers } = applicant;

        const teacher = teachers?.find((t) => t.recommenderId === recommenderId);

        useNotificationStore.getState().notify({
          title: 'Invitation sent',
          message: `${teacher?.firstName as string} ${teacher?.lastName as string} was invited as a recommender.`,
          severity: 'success',
        });
      } finally {
        setLoading('assignTeacher', false, set);
      }
    },

    unassignTeacher: async (recommenderId: number, memberId: number) => {
      setLoading('unassignTeacher', true, set);

      try {
        const { teachers } = get().applicant;
        const recommender = teachers?.find((t) => t.recommenderId === recommenderId);

        const applicant = await commonAppService.patchAsync<
          ApplicantExtended,
          { recommenderId: number; memberId: number }
        >(`unassign-teacher`, {
          recommenderId,
          memberId,
        });

        await get().getFormStatuses();

        set({
          applicant,
        });

        const isTeacherAssignedToAnotherMember = applicant?.members
          ?.filter((member) => member.memberId !== memberId)
          ?.some((member) =>
            member.recommenders?.some(
              (recommender) =>
                recommender.recommenderId === recommenderId && recommender.personaType === PersonaType.TEACHER,
            ),
          );

        useNotificationStore.getState().notify({
          title: 'Teacher removed',
          message: isTeacherAssignedToAnotherMember
            ? `${recommender?.firstName} ${recommender?.lastName} was removed as a recommender for this college.`
            : `${recommender?.firstName} ${recommender?.lastName} was removed from your recommenders list.`,
          severity: 'success',
        });
      } finally {
        setLoading('unassignTeacher', false, set);
      }
    },

    getMembers: async (memberIds: number[]) => {
      setLoading('getMembers', true, set);

      const response = await commonAppService.getAsync<MembersDocument[]>(`members?memberIds=${memberIds.join(',')}`);

      setLoading('getMembers', false, set);
      return response;
    },
  }),

  Object.is,
);
